/**
 * Ringtone (incoming call), ringback (outgoing, waiting for an answer) and the
 * new-message chime, synthesized via the Web Audio API — no audio asset to
 * source, license, or bundle.
 *
 * The ringtone and ringback use different pitch and rhythm so either can be
 * told apart by ear alone (useful if you're not looking at the screen).
 *
 * iPhone: Safari keeps every AudioContext silent until it has been started
 * from inside a tap. A call or message always arrives with no tap in
 * progress, so a context created at that moment never makes a sound. Instead
 * there is one shared context, and every tap anywhere in the app (unlocking,
 * opening a chat...) wakes it up — after that, tones can start at any time.
 * The ring/silent switch still mutes these, like any other app sound.
 */

interface ToneSpec {
  /** One or more frequencies summed into a chord. */
  frequencies: number[];
  onMs: number;
  offMs: number;
  gain: number;
}

type AudioCtor = typeof AudioContext;

let ctx: AudioContext | null = null;

/** The app's one shared AudioContext (also used to play call audio on iPhone). */
export function getAudioContext(): AudioContext | null {
  return audioContext();
}

function audioContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor: AudioCtor | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

/** Resume the shared context from inside a user gesture (see file comment). */
function unlock(): void {
  const c = audioContext();
  if (!c || c.state === "running") return;
  void c.resume().catch(() => undefined);
  // Older iOS versions only count the context as unlocked once something has
  // actually been played from within the gesture.
  const silent = c.createBufferSource();
  silent.buffer = c.createBuffer(1, 1, 22050);
  silent.connect(c.destination);
  silent.start(0);
}

let unlockInstalled = false;
/** Wake the audio engine on every tap/keypress. Call once at startup. */
export function installAudioUnlock(): void {
  if (unlockInstalled) return;
  unlockInstalled = true;
  // Kept for the app's lifetime, not removed after the first tap: iOS
  // suspends the context again after a phone call, Siri, or backgrounding.
  for (const type of ["pointerdown", "touchend", "keydown"] as const) {
    document.addEventListener(type, unlock, { capture: true, passive: true });
  }
}

/** A started tone: stop it, and whether it is actually making a sound. */
export interface Tone {
  stop(): void;
  /** False when the audio engine is suspended/unavailable — caller should vibrate instead. */
  readonly audible: boolean;
}

const SILENT_TONE: Tone = { stop: () => undefined, audible: false };

/**
 * How many on/off cycles are scheduled ahead. Everything is scheduled on the
 * audio clock rather than driven by setTimeout: a suspended context freezes
 * `currentTime` (so every setTimeout-scheduled gain change lands on the same
 * instant), and a backgrounded page throttles timers to once a second or
 * worse — between them the ring used to come out as silence or a stuck drone.
 */
const RING_CYCLES = 60;

function playTone(spec: ToneSpec): Tone {
  const c = audioContext();
  if (!c) return SILENT_TONE;
  void c.resume().catch(() => undefined);

  const master = c.createGain();
  master.gain.value = 0;
  master.connect(c.destination);

  const oscillators = spec.frequencies.map((freq) => {
    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    osc.connect(master);
    osc.start();
    return osc;
  });

  // One gain envelope covering the whole ring, laid out on the audio clock.
  const cycle = (spec.onMs + spec.offMs) / 1000;
  const on = spec.onMs / 1000;
  for (let i = 0; i < RING_CYCLES; i++) {
    const at = c.currentTime + i * cycle;
    master.gain.setTargetAtTime(spec.gain, at, 0.02);
    master.gain.setTargetAtTime(0, at + on, 0.02);
  }

  let stopped = false;
  return {
    get audible() {
      return c.state === "running";
    },
    stop() {
      if (stopped) return;
      stopped = true;
      master.gain.cancelScheduledValues(c.currentTime);
      master.gain.setTargetAtTime(0, c.currentTime, 0.01);
      for (const osc of oscillators) {
        try {
          osc.stop(c.currentTime + 0.05);
        } catch {
          /* already stopped */
        }
      }
      setTimeout(() => master.disconnect(), 100);
    },
  };
}

/** Incoming call: a warm dual-tone chord, ringing 1.5s on / 3s off. */
export function playRingtone(): Tone {
  return playTone({ frequencies: [480, 400], onMs: 1500, offMs: 3000, gain: 0.15 });
}

/** Outgoing call, waiting for the other side to answer: a higher single pulse. */
export function playRingback(): Tone {
  return playTone({ frequencies: [425], onMs: 1000, offMs: 2000, gain: 0.1 });
}

/**
 * Buzz the phone in the pattern of a ring, for when the audio engine is
 * suspended (iOS keeps it suspended until the app is touched, so a call that
 * arrives while the app sits untouched would otherwise ring silently).
 * Returns a stop function; a no-op where vibration isn't supported.
 */
export function vibrateRinging(): () => void {
  if (typeof navigator.vibrate !== "function") return () => undefined;
  const buzz = () => navigator.vibrate?.([600, 200, 600]);
  buzz();
  const timer = setInterval(buzz, 3000);
  return () => {
    clearInterval(timer);
    navigator.vibrate?.(0);
  };
}

let lastChimeAt = 0;
/**
 * New message: a short two-note chime. A burst (a backlog arriving on
 * reconnect) chimes once, not once per message.
 */
export function playMessageTone(): void {
  const now = Date.now();
  if (now - lastChimeAt < 1500) return;
  lastChimeAt = now;

  const c = audioContext();
  if (!c) return;
  void c.resume().catch(() => undefined);

  const start = c.currentTime + 0.01;
  [
    { freq: 880, at: 0 },
    { freq: 1320, at: 0.12 },
  ].forEach(({ freq, at }) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, start + at);
    gain.gain.linearRampToValueAtTime(0.18, start + at + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + at + 0.28);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(start + at);
    osc.stop(start + at + 0.3);
  });
}
